import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import { PresignedUrlResponse } from '@/components/sidebar/Gallery'
import { imageType } from '@/pages/getRecipe/allInOne/utils/constants'

export interface LABColor {
  L: number | string | null
  a: number | string | null
  b: number | string | null
}

export interface CanvasImage {
  url: string
  width: number
  height: number
  calibrated_image: string
  dehazed_image: string
  image: string
  type: string
  id?: string | number
  recipe_data?: {
    dehazingReseted: boolean
  }
  key_image: string
  latest_image: string
  raw_width?: number
  raw_height?: number
}

export interface ImagePixelInfo {
  x: number | null
  y: number | null
  labColor: LABColor | {}
}

export interface DualCanvasImage {
  [PLACEMENT.LEFT]: CanvasImage | null
  [PLACEMENT.RIGHT]: CanvasImage | null
}

export type NewImage = PresignedUrlResponse

export interface AppState {
  canvasImage: any
  dualCanvasImage: DualCanvasImage
  isMultiSession: boolean
  selectedColor: LABColor
  imagePixelInfo: ImagePixelInfo
  greyCardValue: Record<string | number, any>
  imageEdit: {
    [path: string]: boolean
  }
  placement: string | undefined
  activePlan: any | null
  pendingCalibrationIds: string[]
  newImages: NewImage[]
  dentinRecipeIndexByRegion: {
    [workflow: string]: {
      [imageId: string]: {
        [region: string]: number
      }
    }
  }
  frameworkRecipeIndexByWorkflow: {
    [workflow: string]: {
      [imageId: string]: number
    }
  }
  isLoggingOut: boolean
  isImageUploadFailed: boolean
  canvasScale: Record<PLACEMENT, number>
}

export const enum PLACEMENT {
  RIGHT = 'right',
  LEFT = 'left',
  CENTER = 'center',
}

const initialState: AppState = {
  canvasImage: null,
  dualCanvasImage: {
    [PLACEMENT.RIGHT]: null,
    [PLACEMENT.LEFT]: null,
  },
  isMultiSession: false,
  selectedColor: { L: '00.00', a: '00.00', b: '00.00' },
  imagePixelInfo: { x: null, y: null, labColor: {} },
  greyCardValue: {},
  imageEdit: {},
  placement: undefined,
  activePlan: null,
  pendingCalibrationIds: [],
  newImages: [],
  dentinRecipeIndexByRegion: {},
  frameworkRecipeIndexByWorkflow: {},
  isLoggingOut: false,
  isImageUploadFailed: false,
  canvasScale: { left: 1, right: 1, center: 1 },
}

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setCanvasImage: (state, action: PayloadAction<CanvasImage | null>) => {
      state.canvasImage = action.payload
    },
    resetCanvasImage: (state) => {
      state.canvasImage = null
    },
    setDualCanvasImage: (state, action: PayloadAction<any | null>) => {
      if (action.payload === null) {
        state.dualCanvasImage = {
          [PLACEMENT.LEFT]: null,
          [PLACEMENT.RIGHT]: null,
        }
      } else {
        state.dualCanvasImage = {
          ...state.dualCanvasImage,
          ...action.payload,
        }
      }
    },
    resetDualCanvasImage: (state, action) => {
      state.dualCanvasImage = {
        ...state.dualCanvasImage,
        ...action.payload,
      }
    },
    checkAndClearDualCanvas: (state) => {
      const newDualCanvas = { ...state.dualCanvasImage }
      // Check and clear left canvas if not calibrated
      if (
        newDualCanvas.left &&
        !newDualCanvas.left.calibrated_image &&
        newDualCanvas.left.type === imageType.RAW
      ) {
        newDualCanvas.left = null
      }
      // Check and clear right canvas if not calibrated
      if (
        newDualCanvas.right &&
        !newDualCanvas.right.calibrated_image &&
        newDualCanvas.right.type === imageType.RAW
      ) {
        newDualCanvas.right = null
      }

      state.dualCanvasImage = newDualCanvas
    },
    setIsMultiSession: (state, action: PayloadAction<boolean>) => {
      state.isMultiSession = action.payload
    },
    setSelectedColor: (state, action: PayloadAction<LABColor>) => {
      state.selectedColor = action.payload
    },
    setImagePixelInfo: (state, action: PayloadAction<ImagePixelInfo>) => {
      state.imagePixelInfo = action.payload
    },
    clearAppState: (state) => {
      const isLoggingOut = state.isLoggingOut
      Object.assign(state, { ...initialState, isLoggingOut })
    },
    setGreyCardValue: (
      state,
      action: PayloadAction<{ imageId: string | number; value: any }>,
    ) => {
      const { imageId, value } = action.payload
      state.greyCardValue[imageId] = value
    },
    setImageEdit: (
      state,
      action: PayloadAction<{
        imageEdit: boolean
        placement: string | undefined
        path: string
        clearAll?: boolean
      }>,
    ) => {
      const { imageEdit, placement, path, clearAll } = action.payload

      if (clearAll || !imageEdit) {
        Object.keys(state.imageEdit).forEach((key) => {
          state.imageEdit[key] = false
        })
      }

      state.imageEdit[path] = imageEdit
      state.placement = placement
    },
    setActivePlan(state, action: PayloadAction<any>) {
      state.activePlan = action.payload
    },
    markCalibrationInProgress(state, action: PayloadAction<string>) {
      if (!state.pendingCalibrationIds.includes(action.payload)) {
        state.pendingCalibrationIds.push(action.payload)
      }
    },
    clearCalibrationInProgress(state, action: PayloadAction<string>) {
      state.pendingCalibrationIds = state.pendingCalibrationIds.filter(
        (id) => id !== action.payload,
      )
    },
    setNewImages(state, action: PayloadAction<NewImage[]>) {
      state.newImages = action.payload
    },
    removeImageById: (state, action: PayloadAction<string | number>) => {
      state.newImages = state.newImages.filter((img: any) => {
        if (img instanceof File) return true
        return img.id !== action.payload
      })
    },
    removeAllImages: (state) => {
      state.newImages = []
    },
    setRecipeIndex: (state, action) => {
      const { workflow, imageId, region, index } = action.payload
      if (!state.dentinRecipeIndexByRegion[workflow]) {
        state.dentinRecipeIndexByRegion[workflow] = {}
      }
      if (!state.dentinRecipeIndexByRegion[workflow][imageId]) {
        state.dentinRecipeIndexByRegion[workflow][imageId] = {}
      }
      state.dentinRecipeIndexByRegion[workflow][imageId][region] = index
    },
    initializeRecipeIndex: (state, action) => {
      const { workflow, imageId, region } = action.payload
      if (!workflow || imageId === undefined || imageId === null) return

      if (!state.dentinRecipeIndexByRegion) {
        state.dentinRecipeIndexByRegion = {}
      }

      if (!state.dentinRecipeIndexByRegion?.[workflow]?.[imageId]) {
        state.dentinRecipeIndexByRegion[workflow] = {}
      }
      if (!state?.dentinRecipeIndexByRegion?.[workflow]?.[imageId]) {
        state.dentinRecipeIndexByRegion[workflow][imageId] = {}
      }
      if (
        state?.dentinRecipeIndexByRegion?.[workflow]?.[imageId]?.[region] ===
        undefined
      ) {
        state.dentinRecipeIndexByRegion[workflow][imageId][region] = 0
      }
    },
    removeDentinRecipeIndex: (
      state,
      action: PayloadAction<{
        workflow: string
        imageId: string | number
        key: string
      }>,
    ) => {
      const { workflow, imageId, key } = action.payload

      if (state.dentinRecipeIndexByRegion?.[workflow]?.[imageId]) {
        delete state.dentinRecipeIndexByRegion[workflow][imageId][key]

        if (
          Object.keys(state.dentinRecipeIndexByRegion[workflow][imageId])
            .length === 0
        ) {
          delete state.dentinRecipeIndexByRegion[workflow][imageId]
        }
        if (
          Object.keys(state.dentinRecipeIndexByRegion[workflow]).length === 0
        ) {
          delete state.dentinRecipeIndexByRegion[workflow]
        }
      }
    },
    initializeFrameworkRecipeIndex: (state, action) => {
      const { workflow, imageId } = action.payload
      if (!workflow || imageId === undefined || imageId === null) return

      if (!state.frameworkRecipeIndexByWorkflow) {
        state.frameworkRecipeIndexByWorkflow = {}
      }

      if (!state?.frameworkRecipeIndexByWorkflow?.[workflow]) {
        state.frameworkRecipeIndexByWorkflow[workflow] = {}
      }
      if (
        state?.frameworkRecipeIndexByWorkflow?.[workflow]?.[imageId] ===
        undefined
      ) {
        state.frameworkRecipeIndexByWorkflow[workflow][imageId] = 0
      }
    },
    setFrameworkRecipeIndex: (state, action) => {
      const { workflow, imageId, index } = action.payload
      if (!state.frameworkRecipeIndexByWorkflow[workflow]) {
        state.frameworkRecipeIndexByWorkflow[workflow] = {}
      }
      state.frameworkRecipeIndexByWorkflow[workflow][imageId] = index
    },
    removeFrameWorkIndex: (
      state,
      action: PayloadAction<{
        workflow: string
        imageId: string | number
      }>,
    ) => {
      const { workflow, imageId } = action.payload

      if (
        state.frameworkRecipeIndexByWorkflow?.[workflow]?.[imageId] !==
        undefined
      ) {
        delete state.frameworkRecipeIndexByWorkflow[workflow][imageId]
        if (
          Object.keys(state.frameworkRecipeIndexByWorkflow[workflow]).length ===
          0
        ) {
          delete state.frameworkRecipeIndexByWorkflow[workflow]
        }
      }
    },
    setIsLoggingOut: (state, action: PayloadAction<boolean>) => {
      state.isLoggingOut = action.payload
    },
    setImageUploadFailed(state, action: PayloadAction<boolean>) {
      state.isImageUploadFailed = action.payload
    },
    removeFailedImage(state, action: PayloadAction<string>) {
      state.newImages = state.newImages.filter(
        (img) => (img as any)?.presignedData?.key !== action.payload,
      )
    },
    setCanvasScale(
      state,
      action: PayloadAction<{ placement: PLACEMENT; scale: number }>,
    ) {
      state.canvasScale[action.payload.placement] = action.payload.scale
    },
  },
})

export const {
  setCanvasImage,
  setDualCanvasImage,
  setIsMultiSession,
  setSelectedColor,
  clearAppState,
  setImagePixelInfo,
  checkAndClearDualCanvas,
  resetCanvasImage,
  resetDualCanvasImage,
  setGreyCardValue,
  setImageEdit,
  setActivePlan,
  markCalibrationInProgress,
  clearCalibrationInProgress,
  setNewImages,
  removeImageById,
  removeAllImages,
  setRecipeIndex,
  initializeRecipeIndex,
  removeDentinRecipeIndex,
  initializeFrameworkRecipeIndex,
  setFrameworkRecipeIndex,
  removeFrameWorkIndex,
  setIsLoggingOut,
  setImageUploadFailed,
  removeFailedImage,
  setCanvasScale,
} = appSlice.actions

export default appSlice.reducer
